const PERMISSION_POLL_MS_DEFAULT = 60_000;
/** Safari often omits permission change events; poll faster while the tab is visible. */
const PERMISSION_POLL_MS_SAFARI_VISIBLE = 2_000;
const PERMISSION_POLL_MS_SAFARI_HIDDEN = 30_000;
const PERMISSION_HANDLER_DEBOUNCE_MS = 50;
const PERMISSION_POLLER_KEY = '__juicyhousePushPermissionPoller';

export const isSafariBrowser = () => {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent;
  return (
    (/^((?!chrome|android).)*safari/i.test(ua) || /Version\/[\d.]+.*Safari/.test(ua)) &&
    !/Chrome|Chromium|Edg/.test(ua)
  );
};

/** Read permission state whenever Notification API exists — not only when FCM can run. */
const canAccessNotificationPermission = () =>
  typeof window !== 'undefined' &&
  typeof Notification !== 'undefined' &&
  window.isSecureContext;

const getPermissionPollIntervalMs = () => {
  if (!isSafariBrowser()) {
    return PERMISSION_POLL_MS_DEFAULT;
  }
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    return PERMISSION_POLL_MS_SAFARI_HIDDEN;
  }
  return PERMISSION_POLL_MS_SAFARI_VISIBLE;
};

const logWarn = (message, error) => {
  if (process.env.NODE_ENV === 'production') {
    return;
  }
  if (error) {
    console.warn(message, error);
    return;
  }
  console.warn(message);
};

/**
 * Minimum environment for FCM / web push (service worker + IndexedDB + Push API), not merely
 * Notification API. Mirrors the sync checks Firebase performs inside getMessaging() so that
 * in-app browsers (Instagram WKWebView, Facebook, etc.) that expose serviceWorker/IndexedDB but
 * lack PushManager or the required ServiceWorkerRegistration/PushSubscription APIs are excluded
 * before any Firebase call is attempted.
 */
export const canUseWebPush = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  if (typeof Notification === 'undefined') return false;
  if (!window.isSecureContext) return false;
  if (!navigator.serviceWorker) return false;
  if (typeof indexedDB === 'undefined') return false;
  // PushManager is required by Firebase Messaging; absent in many in-app browser WebViews.
  if (!('PushManager' in window)) return false;
  // Firebase also requires showNotification on ServiceWorkerRegistration.
  if (
    typeof ServiceWorkerRegistration !== 'undefined' &&
    !('showNotification' in ServiceWorkerRegistration.prototype)
  ) return false;
  // Firebase requires getKey on PushSubscription.
  if (
    typeof PushSubscription !== 'undefined' &&
    !('getKey' in PushSubscription.prototype)
  ) return false;
  return true;
};

let hasLoggedUnsupportedContext;

const getInitialPermissionSnapshot = () =>
  typeof Notification !== 'undefined' ? Notification.permission : null;

/** One poller per runtime — survives duplicate bundles / microfrontend copies of this module. */
const getPermissionPoller = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  if (!window[PERMISSION_POLLER_KEY]) {
    window[PERMISSION_POLLER_KEY] = {
      listeners: new Set(),
      intervalId: null,
      lastKnown: getInitialPermissionSnapshot(),
    };
  }
  return window[PERMISSION_POLLER_KEY];
};

const runPermissionPoll = () => {
  if (!canAccessNotificationPermission()) {
    return;
  }
  const poller = getPermissionPoller();
  if (!poller) {
    return;
  }

  const current = Notification.permission;
  const permissionChanged = current !== poller.lastKnown;
  if (permissionChanged) {
    poller.lastKnown = current;
  }

  if (!permissionChanged) {
    return;
  }

  for (const listener of poller.listeners) {
    try {
      listener();
    } catch (_) {
      // ignore subscriber errors
    }
  }
};

/** Run the permission poller immediately (e.g. after returning from Safari site settings). */
export const flushPushPermissionPoll = () => {
  runPermissionPoll();
};

const restartPermissionPollInterval = () => {
  const poller = getPermissionPoller();
  if (!poller || typeof window === 'undefined' || poller.listeners.size === 0) {
    return;
  }
  if (poller.intervalId != null) {
    window.clearInterval(poller.intervalId);
  }
  poller.intervalId = window.setInterval(runPermissionPoll, getPermissionPollIntervalMs());
};

let permissionPollVisibilityAttached = false;

const onPermissionPollVisibilityChange = () => {
  restartPermissionPollInterval();
  flushPushPermissionPoll();
};

const attachPermissionPollVisibilityListener = () => {
  if (permissionPollVisibilityAttached || typeof document === 'undefined') {
    return;
  }
  permissionPollVisibilityAttached = true;
  document.addEventListener('visibilitychange', onPermissionPollVisibilityChange);
};

const detachPermissionPollVisibilityListener = () => {
  if (!permissionPollVisibilityAttached || typeof document === 'undefined') {
    return;
  }
  permissionPollVisibilityAttached = false;
  document.removeEventListener('visibilitychange', onPermissionPollVisibilityChange);
};

const addPermissionPollListener = (listener) => {
  const poller = getPermissionPoller();
  if (!poller || typeof window === 'undefined') {
    return () => {};
  }

  poller.listeners.add(listener);
  if (poller.listeners.size === 1) {
    const current = Notification.permission;
    if (current !== poller.lastKnown) {
      poller.lastKnown = current;
      try {
        listener();
      } catch (_) {
        // ignore subscriber errors
      }
    }
    attachPermissionPollVisibilityListener();
    restartPermissionPollInterval();
  }

  return () => {
    poller.listeners.delete(listener);
    if (poller.listeners.size === 0) {
      if (poller.intervalId != null) {
        window.clearInterval(poller.intervalId);
        poller.intervalId = null;
      }
      detachPermissionPollVisibilityListener();
    }
  };
};

/**
 * Read current Notification permission without prompting or side effects.
 * Never call Notification.requestPermission() from background sync — iOS Safari may re-show
 * the prompt on every focus/visibility if default and sync runs repeatedly.
 *
 * @returns {'default' | 'granted' | 'denied' | 'unsupported'}
 */
export const getPushPermissionState = () => {
  if (!canAccessNotificationPermission()) {
    if (!hasLoggedUnsupportedContext) {
      hasLoggedUnsupportedContext = true;
      logWarn('pushNotification >> Notifications require a secure context and Notification API support.');
    }
    return 'unsupported';
  }
  return Notification.permission;
};

/**
 * Resolves permission for FCM sync. Safari can leave Notification.permission as "default"
 * after the user allows alerts in Website Settings while Permissions API already reports granted.
 *
 * @returns {Promise<'default' | 'granted' | 'denied' | 'unsupported'>}
 */
export const resolvePushPermissionState = async () => {
  if (!canAccessNotificationPermission()) {
    return 'unsupported';
  }

  const direct = Notification.permission;
  if (direct === 'granted' || direct === 'denied') {
    return direct;
  }

  // Safari can leave Notification.permission as "default" after the user allows alerts
  // in Website Settings; Permissions API reflects the real state. Other browsers keep
  // Notification.permission in sync — do not treat Permissions API alone as granted.
  if (isSafariBrowser()) {
    try {
      const status = await navigator.permissions?.query?.({ name: 'notifications' });
      if (status?.state === 'granted' || status?.state === 'denied') {
        return status.state;
      }
    } catch (_) {
      // Safari / older browsers may reject notifications permission query.
    }
  }

  return direct;
};

/**
 * Request notification permission — must run from a user gesture on iOS (tap, etc.).
 */
export const requestWebPushPermissionFromUserGesture = async () => {
  if (!canAccessNotificationPermission()) {
    return 'unsupported';
  }
  const existing = Notification.permission;
  if (existing === 'granted' || existing === 'denied') {
    return existing;
  }
  return Notification.requestPermission();
};

const attachPermissionStatusListener = (status, handler) => {
  const previousOnchange = status.onchange;
  const onchangeWrapper = (event) => {
    if (typeof previousOnchange === 'function') {
      try {
        previousOnchange.call(status, event);
      } catch (_) {
        // ignore chained handler errors
      }
    }
    handler(event);
  };

  status.onchange = onchangeWrapper;
  if (typeof status.addEventListener === 'function') {
    status.addEventListener('change', handler);
  }

  return () => {
    try {
      if (status.onchange === onchangeWrapper) {
        status.onchange = previousOnchange ?? null;
      }
      status.removeEventListener?.('change', handler);
    } catch (_) {
      // ignore
    }
  };
};

/**
 * Subscribe to Notification permission updates that happen outside the page (e.g. user flipped
 * Allow in Chrome / Android site settings). Uses Permissions API where available, with polling
 * fallback for Safari / WebViews where query or change events are unreliable.
 *
 * @param {() => void} onPermissionMayHaveChanged — should schedule FCM sync (debounced); never request permission here.
 * @returns {() => void} cleanup
 */
export const subscribePushPermissionChanges = (onPermissionMayHaveChanged) => {
  if (typeof window === 'undefined' || typeof onPermissionMayHaveChanged !== 'function') {
    return () => {};
  }
  if (!canAccessNotificationPermission()) {
    return () => {};
  }

  let disposed = false;
  let removeQueryListener = () => {};
  let handlerTimeout;

  const handler = () => {
    if (disposed) {
      return;
    }
    clearTimeout(handlerTimeout);
    handlerTimeout = setTimeout(() => {
      if (disposed) {
        return;
      }
      if (!disposed) {
        onPermissionMayHaveChanged();
      }
    }, PERMISSION_HANDLER_DEBOUNCE_MS);
  };

  const removePollListener = addPermissionPollListener(handler);

  (async () => {
    try {
      const permissions = navigator.permissions;
      if (!permissions?.query) {
        return;
      }
      const status = await permissions.query({ name: 'notifications' });
      if (disposed) {
        return;
      }
      if (!status) {
        return;
      }
      removeQueryListener = attachPermissionStatusListener(status, handler);
    } catch (_) {
      // Safari / older browsers may reject notifications permission query.
    }
  })();

  return () => {
    disposed = true;
    clearTimeout(handlerTimeout);
    removeQueryListener();
    removePollListener();
  };
};
